import React, { FC, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Divider from '@material-ui/core/Divider'
import Button from 'UI/Button/ButtonWithPreloader'
import ModalComponent from 'UI/Modal'
import { useSnackbar } from 'notistack'
import { CLIENT_ALREADY_ON_YOUR_LIST, SOMETHING_WENT_WRONG } from 'constants/errors'
import { useClientSearch, useAddExistingClient } from 'hooks/useClientSearch'
import { IClientExistingDto } from 'typescript/interfaces/clients'
import { useFormik } from 'formik'
import { schemaSearchExistingClient } from './schema'

interface Props {
  open: boolean
  onClose: (refresh?: boolean) => void
}

const SearchExistingClientModal: FC<Props> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { search, results, isLoading, reset } = useClientSearch(() =>
    enqueueSnackbar(SOMETHING_WENT_WRONG)
  )

  const { addClient, isLoading: isLoadingAdd } = useAddExistingClient(
    () => {
      enqueueSnackbar('Client successfully added')
      onClose(true)
    },
    err => {
      if (
        err.response &&
        (err.response.data as any).description === 'CLIENT_ALREADY_ON_YOUR_LIST'
      ) {
        enqueueSnackbar(CLIENT_ALREADY_ON_YOUR_LIST)
      } else {
        enqueueSnackbar(SOMETHING_WENT_WRONG)
      }
    }
  )

  const {
    handleSubmit,
    values,
    touched,
    errors,
    handleChange,
    isValid,
    dirty,
    resetForm,
  } = useFormik({
    initialValues: { ssn: '', companyName: '' },
    validationSchema: schemaSearchExistingClient,
    onSubmit: vals => {
      search(vals)
    },
  })

  // Reset form and search results when modal opens/closes
  useEffect(() => {
    if (!open) {
      resetForm()
      reset()
    }
  }, [open, resetForm, reset])

  const handleClose = () => {
    onClose() // Don't always trigger refresh
    reset()
    resetForm()
  }

  const handleAddClient = (client: IClientExistingDto) => {
    addClient({
      businessName: client.businessName,
      email: client.email,
      businessPhone: client.businessPhone,
    })
  }

  const searchedCompanyName = values.companyName.trim().toLowerCase()
  const isCompanyMatch = (businessName: string) =>
    Boolean(searchedCompanyName) &&
    (businessName || '').toLowerCase().includes(searchedCompanyName)

  return (
    <ModalComponent open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <Box minWidth={480} maxWidth={700}>
          <Box mb="1.5rem">
            <Typography variant="h3" color="textSecondary">
              Search Existing Client
            </Typography>
          </Box>
          <Box display="grid" gridGap="1.5rem">
            <TextField
              label="SSN"
              name="ssn"
              value={values.ssn}
              onChange={e => {
                const onlyDigits = e.target.value.replace(/[^0-9]/g, '')
                handleChange({ ...e, target: { ...e.target, value: onlyDigits, name: 'ssn' } })
              }}
              required
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              error={Boolean(touched.ssn && errors.ssn)}
              helperText={touched.ssn && (errors.ssn as string)}
            />
            <TextField
              label="Company Name (optional)"
              name="companyName"
              value={values.companyName}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              error={Boolean(touched.companyName && errors.companyName)}
              helperText={touched.companyName && (errors.companyName as string)}
            />
            <Box display="flex" justifyContent="flex-end" mt="0.5rem">
              <Button
                disabled={!dirty || !isValid}
                loading={isLoading}
                color="primary"
                variant="contained"
                type="submit"
              >
                Search
              </Button>
              <Box ml="1rem">
                <Button onClick={handleClose} color="primary" variant="outlined">
                  Close
                </Button>
              </Box>
            </Box>
          </Box>
          <Divider style={{ margin: '1.5rem 0' }} />
          <Box>
            <Typography variant="h4" color="textSecondary" gutterBottom>
              Found Client
            </Typography>
            {results === undefined ? (
              <Typography color="textSecondary">Enter data and press Search</Typography>
            ) : results === null || results.length === 0 ? (
              <Typography color="textSecondary">No client found</Typography>
            ) : (
              <Box display="grid" gridRowGap="1rem">
                {results.map((item: IClientExistingDto) => (
                  <Box
                    key={item.email}
                    p="1rem"
                    border={
                      isCompanyMatch(item.businessName)
                        ? '2px solid #3D5AFE'
                        : '1px solid #E6E9F0'
                    }
                    borderRadius={4}
                  >
                    <Box display="grid" gridRowGap="0.5rem">
                      {isCompanyMatch(item.businessName) && (
                        <Typography variant="caption" style={{ color: '#3D5AFE' }}>
                          Matches company name
                        </Typography>
                      )}
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Business Name</Typography>
                        <Typography>{item.businessName}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Business Phone</Typography>
                        <Typography>{item.businessPhone}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Cell Phone</Typography>
                        <Typography>{item.cellPhone}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Email</Typography>
                        <Typography>{item.email}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="textSecondary">Fax</Typography>
                        <Typography>{item.fax}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" mt="0.5rem">
                        <Button
                          onClick={() => handleAddClient(item)}
                          loading={isLoadingAdd}
                          color="primary"
                          variant="contained"
                        >
                          Add Client
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </form>
    </ModalComponent>
  )
}

export default SearchExistingClientModal
